import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  pickLocalized,
  questionTimeLimitMs,
  type PublicQuestion,
  type ServerMessage,
} from "@binogi-livequiz/shared";
import { useTranslation } from "react-i18next";
import { AnswerTile } from "../components/AnswerTile";
import { TopTimerBar } from "../components/TopTimerBar";
import { ScreenHeader } from "../components/layout/ScreenHeader";
import { Button } from "../components/ui/Button";
import { serverErrorKey } from "../lib/i18n";
import { useWebSocket } from "../lib/ws-context";
import { loadStudentSession } from "../lib/session-storage";
import { useStudentRejoin } from "../hooks/useStudentRejoin";

interface RouteState {
  sessionCode: string;
  displayName: string;
  questionIndex: number;
  totalQuestions: number;
  timeLimitMs: number;
  question: PublicQuestion;
  startedAt: number;
  availableLanguages?: string[];
}

export default function PlayQuestion() {
  const navigate = useNavigate();
  const location = useLocation();
  const ws = useWebSocket();
  const { t, i18n } = useTranslation();
  const state = location.state as RouteState | null;

  // A socket that drops mid-question (phone lock, app switch) used to
  // leave this screen with no way back in: the next tap opened a fresh
  // socket and SUBMIT_ANSWER went out un-joined.
  // `myName` is the name the server currently knows us by — a rejoin can
  // come back with a different one (name collision), and every downstream
  // screen resolves its leaderboard row by name.
  const { displayName: myName, superseded } = useStudentRejoin(state);

  const [selected, setSelected] = useState<string[] | null>(null);
  const [submitted, setSubmitted] = useState(false);
  // useState updates are batched and don't propagate synchronously, so
  // a rapid double-tap (wrong then correct) could run the submit
  // handler twice — the first commit wins server-side but the local
  // `selected` ends up reflecting the *second* tap, which then drove
  // PlayBetween's headline to "Correct!" with a score of 0. Mirror
  // `submitted` into a ref so the guard fires the instant the first
  // tap lands.
  const submittedRef = useRef(false);
  const [outOfTime, setOutOfTime] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMulti = state?.question.type === "multiChoice";

  // Pick the language the student sees question text in. lockedEn forces
  // English regardless of the student's i18n switcher; studentChoice (or
  // absent) follows whatever locale the student is currently on.
  const studentSession = loadStudentSession();
  const displayLang =
    studentSession?.languageMode === "lockedEn" ? "en" : i18n.language;
  const langPickerCodes =
    state?.availableLanguages ?? studentSession?.availableLanguages;

  // Reset state when new question state arrives — including a catch-up
  // QUESTION_START after a rejoin (same index, fresh startedAt), which
  // must clear a stale "Answered!" from a tap the server never saw.
  useEffect(() => {
    setSelected(null);
    setSubmitted(false);
    submittedRef.current = false;
    setOutOfTime(false);
    setError(null);
  }, [state?.questionIndex, state?.startedAt]);

  // Watch for QUESTION_END / SESSION_FINISHED / new QUESTION_START.
  useEffect(() => {
    const m = ws.lastMessage;
    if (!m || !state) return;
    handle(m);
    function handle(msg: ServerMessage) {
      if (msg.type === "QUESTION_END" && msg.questionIndex === state!.questionIndex) {
        ws.consume();
        navigate("/play/between", {
          state: {
            sessionCode: state!.sessionCode,
            displayName: myName,
            questionIndex: msg.questionIndex,
            correctOids: msg.correctOids,
            results: msg.results,
            leaderboard: msg.leaderboard,
            mySelection: selected,
            availableLanguages: state!.availableLanguages,
          },
        });
      } else if (msg.type === "SESSION_FINISHED") {
        ws.consume();
        navigate("/play/finished", {
          state: {
            displayName: myName,
            finalLeaderboard: msg.finalLeaderboard,
            availableLanguages: state!.availableLanguages,
          },
        });
      } else if (msg.type === "QUESTION_START") {
        // Should not normally land here, but handle gracefully.
        ws.consume();
        navigate("/play/question", {
          state: {
            sessionCode: state!.sessionCode,
            displayName: myName,
            questionIndex: msg.questionIndex,
            totalQuestions: msg.totalQuestions,
            timeLimitMs: questionTimeLimitMs(msg),
            question: msg.question,
            startedAt: Date.now(),
            availableLanguages: state!.availableLanguages,
          },
          replace: true,
        });
      } else if (msg.type === "ERROR") {
        ws.consume();
        // ALREADY_ANSWERED is not a failure — it means the server *did*
        // record our answer. It surfaces when a catch-up QUESTION_START
        // after a rejoin resets this screen (same index, fresh startedAt)
        // and the student, seeing live tiles again, taps a second time.
        // Restore the "Answered!" state instead of flashing red at a
        // student who did nothing wrong; keep `selected` as tapped so the
        // dim/highlight still reads correctly.
        if (msg.code === "ALREADY_ANSWERED") {
          submittedRef.current = true;
          setSubmitted(true);
          return;
        }
        setError(serverErrorKey(msg));
      }
    }
  }, [myName, navigate, selected, state, ws]);

  const submit = useCallback(
    (oids: string[]) => {
      if (!state || submittedRef.current) return;
      submittedRef.current = true;
      setSelected(oids);
      setSubmitted(true);
      // Tap-relative elapsed time. The server uses this for scoring so
      // a SUBMIT_ANSWER that drains from a slow-network buffer 2s
      // after the timer expired still credits the student — they
      // tapped on time, just couldn't get the bytes out.
      const clientAnswerMs = Date.now() - state.startedAt;
      ws.send({
        type: "SUBMIT_ANSWER",
        questionIndex: state.questionIndex,
        // Which question we believe we're answering. The server rejects a
        // submit whose qid disagrees with its scoring entry at this index
        // rather than crediting the wrong question — cheap insurance
        // against index drift between a catch-up and a fresh start.
        qid: state.question.qid,
        oid: isMulti ? oids : (oids[0] ?? ""),
        clientAnswerMs,
      });
    },
    [isMulti, state, ws],
  );

  const handleTileClick = useCallback(
    (oid: string) => {
      if (submittedRef.current || outOfTime || !state) return;
      if (isMulti) {
        setSelected((prev) => {
          const next = prev ? [...prev] : [];
          const i = next.indexOf(oid);
          if (i >= 0) next.splice(i, 1);
          else next.push(oid);
          return next;
        });
      } else {
        submit([oid]);
      }
    },
    [isMulti, outOfTime, state, submit],
  );

  const handleConfirmMulti = useCallback(() => {
    if (!selected || selected.length === 0) return;
    submit(selected);
  }, [selected, submit]);

  const handleTimeout = useCallback(() => {
    if (!submitted) {
      setOutOfTime(true);
    }
  }, [submitted]);

  if (!state) {
    return (
      <main className="min-h-full flex items-center justify-center px-6 text-center">
        <div>
          <p className="text-white/70">{t("play.no_question")}</p>
          <Button
            onClick={() => navigate("/join")}
            className="mt-4 bg-brand-purple hover:brightness-110"
          >
            {t("play.back_to_join")}
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-full px-4 md:px-6 py-6 md:py-8 max-w-2xl mx-auto flex flex-col">
      <ScreenHeader
        short
        wordmark="sm"
        available={langPickerCodes}
        className="mb-4"
        trailing={
          <div className="text-xs text-white/60">
            {t("play.question.progress", {
              index: state.questionIndex + 1,
              total: state.totalQuestions,
            })}
          </div>
        }
      />

      {superseded && (
        <p
          role="status"
          className="mb-4 rounded-xl border border-brand-amber/50 bg-brand-amber/20 px-4 py-3 text-center text-sm font-semibold"
        >
          {t("play.superseded")}
        </p>
      )}

      <TopTimerBar
        totalMs={state.timeLimitMs}
        startedAt={state.startedAt}
        onComplete={handleTimeout}
        paused={submitted || outOfTime}
      />

      <h1 className="text-xl md:text-2xl font-bold leading-snug mb-5">
        {pickLocalized(state.question.text, displayLang)}
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {state.question.options.map((opt, i) => {
          const isSelected = !!selected?.includes(opt.oid);
          const isDimmed = submitted && !isSelected;
          return (
            <AnswerTile
              key={opt.oid}
              index={i}
              text={pickLocalized(opt.text, displayLang)}
              selected={isSelected}
              dimmed={isDimmed}
              disabled={submitted || outOfTime}
              onClick={() => handleTileClick(opt.oid)}
            />
          );
        })}
      </div>

      {isMulti && !submitted && !outOfTime && (
        <Button
          onClick={handleConfirmMulti}
          disabled={!selected || selected.length === 0}
          className="bg-brand-amber text-brand-ink font-bold hover:brightness-110"
        >
          {selected && selected.length > 1
            ? t("play.question.submit_n", { count: selected.length })
            : t("play.question.submit")}
        </Button>
      )}

      {submitted && (
        <p className="text-center text-brand-good font-semibold animate-pop">
          {t("play.question.answered")}
        </p>
      )}

      {outOfTime && !submitted && (
        <p className="text-center text-brand-bad font-semibold">
          {t("play.question.times_up")}
        </p>
      )}

      {error && (
        <p className="mt-3 text-center text-sm text-brand-bad">{t(error)}</p>
      )}
    </main>
  );
}
